import Maybe from 'graphql/tsutils/Maybe'

import { PostTypeSeo, Scalars } from '@thg-commerce/enterprise-network'

import { MetaRenderer, RendererTags } from '../MetaRenderer'
import { getRobotsMeta } from '../utils'
import { ArticleMetadataTransformer } from '../../../helpers/MetadataTransformer'

export interface ArticleHeadRendererProps {
  seo?: PostTypeSeo
  featuredImage?: {
    mediaItemUrl?: Maybe<Scalars['String']>
    mediaDetails?: {
      width?: Maybe<number>
      height?: Maybe<number>
    }
  }
  articleUrl: string
  defaultLocale: string
  i18nText: {
    defaultMetaDescription: string
  }
}

export type ArticleMetaNameType = {
  'apple-itunes-app': string
  description: string
  robots: string
  googlebot?: string
  bingbot?: string
  'twitter:card': string
  'twitter:creator': string
  'twitter:site': string
}

export type ArticleMetaPropertyType = {
  'og:locale': string
  'og:type': string
  'og:title': string
  'og:description': string
  'og:url': string
  'og:site_name': string
  'article:publisher': string
  'article:published_time': string
  'og:image': string
  'og:image:width': string
  'og:image:height': string
}

export type ArticleMetaLinkType = {
  canonical: string
}

export interface ArticleMetaTags extends Partial<RendererTags> {
  metaNameTags: ArticleMetaNameType
  metaTags: ArticleMetaPropertyType
  linkTags: ArticleMetaLinkType
}

// Used in transformer method to only return valid data
export interface PartialArticleMetaTags
  extends Omit<ArticleMetaTags, 'metaNameTags' | 'metaTags' | 'linkTags'> {
  metaNameTags: Partial<ArticleMetaNameType>
  metaTags: Partial<ArticleMetaPropertyType>
  linkTags: Partial<ArticleMetaLinkType>
}

export const ArticleMetaRenderer = ({
  seo,
  featuredImage,
  articleUrl,
  defaultLocale,
  i18nText,
}: ArticleHeadRendererProps) => {
  const articleMetaTags: ArticleMetaTags = {
    metaNameTags: {
      'apple-itunes-app': `app-id=${seo?.appStoreId}, affiliate-data=${seo?.iTunesAffiliateCode}`,
      description: seo?.metaDesc || i18nText.defaultMetaDescription,
      robots: getRobotsMeta(seo?.metaRobotsNoindex, seo?.metaRobotsNofollow),
      'twitter:card': seo?.twitterExtraFields?.twitterCardType || '',
      'twitter:creator': seo?.twitterExtraFields?.twitterSite
        ? `@${seo?.twitterExtraFields?.twitterSite}`
        : '',
      'twitter:site': seo?.twitterExtraFields?.twitterSite
        ? `@${seo?.twitterExtraFields?.twitterSite}`
        : '',
    },
    metaTags: {
      'og:locale': defaultLocale,
      'og:type': seo?.opengraphType || '',
      'og:title': seo?.opengraphTitle || '',
      'og:description': seo?.opengraphDescriptionDecoded || '',
      'og:url': articleUrl,
      'og:site_name': seo?.opengraphSiteName || '',
      'article:publisher': seo?.opengraphPublisher || '',
      'article:published_time': seo?.opengraphPublishedTime || '',
      'og:image': featuredImage?.mediaItemUrl || '',
      'og:image:width': featuredImage?.mediaDetails?.width?.toString() || '',
      'og:image:height': featuredImage?.mediaDetails?.height?.toString() || '',
    },
    linkTags: {
      canonical: articleUrl,
    },
  }

  return MetaRenderer(ArticleMetadataTransformer(articleMetaTags))
}
